import { useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import type { ClientCampaignCollaboration } from '@/api/beatlyApi';
import { getDeliveryAddressPopover, type DeliveryAddressPopoverConfig } from '@/utils/deliveryAddressPopover';

function collaborationToDeliveryInput(c: ClientCampaignCollaboration) {
  const raw = c.delivery_address;
  const addr =
    typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : '–';
  return {
    address: addr,
    product_delivery: c.product_delivery ?? null,
    address_available: addr !== '–',
  };
}

/**
 * Shipping / digital delivery address for an approved profile card (GET collaborations merge).
 */
export function ProfileDeliveryAddressRow({
  collaboration,
}: {
  collaboration: ClientCampaignCollaboration | undefined;
}) {
  const { t } = useTranslation();
  const [popover, setPopover] = useState<{ top: number; left: number } | null>(null);

  if (!collaboration) {
    return null;
  }

  const input = collaborationToDeliveryInput(collaboration);
  const deliveryPopover: DeliveryAddressPopoverConfig | null = getDeliveryAddressPopover(input);

  return (
    <>
      <div className="relative z-20 mt-3 min-w-0">
        <div className="text-[9px] font-semibold uppercase leading-tight tracking-[0.06em] text-[#98a2b3]">
          {t('contentReview.columns.address')}
        </div>
        {deliveryPopover?.interactive ? (
          <div className="mt-0.5 min-w-0">
            <button
              type="button"
              className="block max-w-full truncate whitespace-nowrap text-left text-[11px] font-semibold text-[#101828] underline decoration-[#d0d5dd] decoration-1 underline-offset-2 hover:text-[#be43ff] hover:decoration-[#be43ff]"
              onClick={(e) => {
                e.stopPropagation();
                if (popover) {
                  setPopover(null);
                  return;
                }
                const r = e.currentTarget.getBoundingClientRect();
                setPopover({ top: r.bottom + 8, left: r.left });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (popover) {
                    setPopover(null);
                    return;
                  }
                  const r = e.currentTarget.getBoundingClientRect();
                  setPopover({ top: r.bottom + 8, left: r.left });
                }
              }}
              title={deliveryPopover.triggerLabel}
            >
              {deliveryPopover.triggerLabel}
            </button>
          </div>
        ) : deliveryPopover ? (
          <div className="mt-0.5 truncate text-[11px] font-semibold text-[#667085]">{deliveryPopover.triggerLabel}</div>
        ) : (
          <div className="mt-0.5 truncate text-[11px] font-semibold text-[#667085]">
            {t('contentReview.delivery.noAddressYet')}
          </div>
        )}
      </div>

      {popover &&
        deliveryPopover?.interactive &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80] bg-transparent" aria-hidden onClick={() => setPopover(null)} />
            <motion.div
              role="dialog"
              aria-label={deliveryPopover.title}
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="fixed z-[100] w-[min(28rem,calc(100vw-1.5rem))] rounded-2xl border border-[#e8eaed] bg-white p-5 text-left shadow-[0_20px_50px_rgba(15,23,42,0.16),0_8px_24px_rgba(15,23,42,0.08)]"
              style={{
                top: popover.top,
                left: (() => {
                  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
                  const maxLeft = vw - Math.min(448, vw - 24) - 12;
                  return Math.max(12, Math.min(popover.left, maxLeft));
                })(),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#f4e8ff]">
                  <MapPin className="h-5 w-5 text-[#be43ff]" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#667085]">{deliveryPopover.title}</p>
                  <p className="mt-2 whitespace-pre-wrap break-words text-[14px] font-medium leading-relaxed text-[#101828] [overflow-wrap:anywhere]">
                    {deliveryPopover.body}
                  </p>
                </div>
              </div>
            </motion.div>
          </>,
          document.body,
        )}
    </>
  );
}
